import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { api } from '../utils/api'
import { Printer } from 'lucide-react'

interface TicketApprovalPrint {
  ticketNumber: number; title: string; description: string | null; createdAt: string
  requester: { displayName: string | null; username: string } | null
  company: { name: string; logo: string | null } | null
  category: { name: string } | null
  subcategory: { name: string } | null
  approvals: {
    id: string; status: 'PENDING' | 'APPROVED' | 'REJECTED'; comment: string | null; respondedAt: string | null
    signatureUrl: string | null; signedByName: string | null
    approver: { displayName: string | null; username: string }
  }[]
}

const STATUS_LABEL: Record<string, string> = { PENDING: 'รอพิจารณา', APPROVED: 'อนุมัติ', REJECTED: 'ไม่อนุมัติ' }

export default function TicketApprovalPrintPage() {
  const { id } = useParams()

  const { data: ticket, isLoading } = useQuery<TicketApprovalPrint>({
    queryKey: ['ticket-approval-print', id],
    queryFn: () => api.get(`/tickets/${id}`).then(r => r.data),
    enabled: !!id,
  })

  const fmt = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '.......... / .......... / ..........'

  if (isLoading) return <div className="flex items-center justify-center h-screen text-gray-400">กำลังโหลด...</div>
  if (!ticket)    return <div className="flex items-center justify-center h-screen text-gray-400">ไม่พบ Ticket</div>

  const approvals = ticket.approvals || []

  return (
    <>
      <div className="print:hidden fixed top-4 right-4 z-50 flex gap-2">
        <button onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium shadow-lg hover:bg-blue-700">
          <Printer size={16}/> พิมพ์ / Export PDF
        </button>
        <button onClick={() => window.close()}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm hover:bg-gray-200">
          ปิด
        </button>
      </div>

      <div className="min-h-screen bg-gray-100 print:bg-white py-8 print:py-0">
        <div className="mx-auto bg-white shadow-lg print:shadow-none flex flex-col"
          style={{ width: '210mm', minHeight: '297mm', padding: '15mm 15mm 10mm 15mm' }}>

          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {ticket.company?.logo ? (
                <img src={ticket.company.logo} alt="logo" className="h-14 max-w-[120px] object-contain"/>
              ) : (
                <div className="w-14 h-14 rounded-full border-2 border-gray-800 flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-800">{ticket.company?.name?.slice(0, 3).toUpperCase() || 'CO'}</span>
                </div>
              )}
            </div>
            <h1 className="text-xl font-bold text-center flex-1">ใบขออนุมัติ (Approval Request)</h1>
            <div className="w-14 text-right text-xs text-gray-500">#{String(ticket.ticketNumber).padStart(4, '0')}</div>
          </div>

          {/* Info table */}
          <table className="w-full border-collapse border border-gray-800 text-xs mb-3">
            <tbody>
              <tr>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 w-28">หัวข้อคำขอ</td>
                <td className="border border-gray-800 px-2 py-1" colSpan={3}>{ticket.title}</td>
              </tr>
              <tr>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1">ผู้แจ้ง</td>
                <td className="border border-gray-800 px-2 py-1">{ticket.requester?.displayName || ticket.requester?.username || '—'}</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 w-24">วันที่แจ้ง</td>
                <td className="border border-gray-800 px-2 py-1">{fmt(ticket.createdAt)}</td>
              </tr>
              <tr>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1">บริษัท / หน่วยงาน</td>
                <td className="border border-gray-800 px-2 py-1">{ticket.company?.name || '—'}</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1">หมวดหมู่</td>
                <td className="border border-gray-800 px-2 py-1">
                  {ticket.category ? `${ticket.category.name}${ticket.subcategory ? ` › ${ticket.subcategory.name}` : ''}` : '—'}
                </td>
              </tr>
              {ticket.description && (
                <tr>
                  <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1">รายละเอียด</td>
                  <td className="border border-gray-800 px-2 py-1" colSpan={3}>{ticket.description.replace(/<[^>]+>/g, ' ')}</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Approval signatures */}
          <p className="text-xs font-bold text-gray-600 mb-2">รายชื่อผู้อนุมัติ</p>
          <div className="grid grid-cols-2 gap-x-10 gap-y-8 text-xs">
            {approvals.map(a => (
              <div key={a.id}>
                <div className="h-16 mb-1 flex items-end justify-center">
                  {a.signatureUrl && <img src={a.signatureUrl} alt="signature" className="max-h-16 object-contain"/>}
                </div>
                <p className="mb-1 flex items-baseline gap-1">
                  <span className="flex-shrink-0">{a.approver.displayName || a.approver.username}</span>
                  <span className="flex-1 border-b border-gray-800 h-3"/>
                </p>
                <p className="text-gray-500">
                  ผลการพิจารณา: {STATUS_LABEL[a.status]}
                  {a.signatureUrl && a.signedByName ? ` · เซ็นโดย ${a.signedByName}` : ''}
                </p>
                <p className="mt-1">วันที่ {fmt(a.respondedAt)}</p>
              </div>
            ))}
            {approvals.length === 0 && (
              <p className="col-span-2 text-center text-gray-400 py-4">ยังไม่มีการกำหนดผู้อนุมัติ</p>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { margin: 0; }
        }
      `}</style>
    </>
  )
}
